import { assetManifest } from "../../assets/manifest";
import type { SupplyCrateCardView } from "../../game/crates/view";
import type { GameSnapshot } from "../../game/runtime/types";
import type { OrbitForgeRuntime } from "../../game/runtime/runtime";
import type { SupplyCrateId, SupplyCrateResult } from "../../game/save/types";
import { formatCompact } from "../../utils/math";
import { crateRewardChips, effectChip, formatDuration, resultTitle, textSpan } from "./crate-display";

const AUTO_REVEAL_MS = 1700;

export function createCratesPanel(runtime: OrbitForgeRuntime): HTMLElement {
  const fragment = document.createElement("div");
  fragment.className = "of-crates-root";

  const panel = document.createElement("section");
  panel.className = "of-panel of-crates-panel";
  panel.setAttribute("aria-label", "Supply Crates");

  const overlay = document.createElement("section");
  overlay.className = "of-crate-reveal-modal";
  overlay.setAttribute("aria-label", "Supply Crate reveal");

  fragment.append(panel, overlay);

  let renderedSignature = "";
  let renderedOverlaySignature = "";
  let autoRevealResultId = 0;
  let autoRevealTimer = 0;
  let wasPanelOpen = false;
  let wasOverlayOpen = false;

  runtime.subscribe((snapshot) => {
    const panelOpen = snapshot.activePanel === "crates";
    const overlayOpen = snapshot.crates.pendingResult !== null;
    if (panelOpen !== wasPanelOpen) {
      panel.classList.toggle("is-open", panelOpen);
      wasPanelOpen = panelOpen;
    }
    if (overlayOpen !== wasOverlayOpen) {
      overlay.classList.toggle("is-open", overlayOpen);
      wasOverlayOpen = overlayOpen;
    }

    const signature = panelOpen ? cratesSignature(snapshot) : renderedSignature;
    if (panelOpen && signature !== renderedSignature) {
      renderPanel(panel, snapshot, runtime);
      renderedSignature = signature;
    }

    const overlaySignature = overlayOpen ? crateOverlaySignature(snapshot) : "";
    if (overlaySignature !== renderedOverlaySignature) {
      renderOverlay(overlay, snapshot, runtime);
      renderedOverlaySignature = overlaySignature;
    }

    const pending = snapshot.crates.pendingResult;
    if (pending && !pending.presented && pending.result.resultId !== autoRevealResultId) {
      window.clearTimeout(autoRevealTimer);
      autoRevealResultId = pending.result.resultId;
      autoRevealTimer = window.setTimeout(() => runtime.dispatch({ type: "present-crate-result" }), AUTO_REVEAL_MS);
    }
  });

  return fragment;
}

function cratesSignature(snapshot: GameSnapshot): string {
  const last = snapshot.crates.lastResult?.result;
  return [
    snapshot.activePanel,
    snapshot.save.stats.Credits.toFixed(0),
    snapshot.crates.freeDrop.remainingSeconds,
    snapshot.crates.pendingBlocksOpen ? 1 : 0,
    last ? `${last.resultId}:${last.presentedAtUnixSeconds ?? "sealed"}` : "none",
    ...snapshot.crates.paidCrates.map((crate) => `${crate.id}:${crate.price}:${crate.remainingOpens}:${crate.resetsInSeconds}:${crate.canOpen ? 1 : 0}`)
  ].join("|");
}

function crateOverlaySignature(snapshot: GameSnapshot): string {
  const pending = snapshot.crates.pendingResult;
  if (!pending) {
    return "";
  }

  return [
    pending.result.resultId,
    pending.presented ? 1 : 0,
    pending.result.jackpot ? 1 : 0,
    pending.result.crateId,
    pending.result.cash,
    pending.rewardLines.join("|")
  ].join(":");
}

function renderPanel(panel: HTMLElement, snapshot: GameSnapshot, runtime: OrbitForgeRuntime): void {
  const title = document.createElement("h2");
  title.className = "of-panel-title";
  title.textContent = "CRATES";

  const header = document.createElement("div");
  header.className = "of-panel-header of-crates-header";
  header.append(title);

  const free = createFreeDrop(snapshot, runtime);
  const list = document.createElement("div");
  list.className = "of-crate-list";
  list.append(free, ...snapshot.crates.paidCrates.map((crate) => createPaidCrateCard(crate, runtime)));

  panel.replaceChildren(header, list);
}

function createFreeDrop(snapshot: GameSnapshot, runtime: OrbitForgeRuntime): HTMLElement {
  const row = document.createElement("article");
  row.className = "of-crate-row";
  row.dataset.crateId = "free";
  row.classList.toggle("is-ready", snapshot.crates.freeDrop.canOpen);

  const icon = createCrateIcon("free");

  const details = document.createElement("div");
  details.className = "of-crate-details";

  const nameLine = document.createElement("div");
  nameLine.className = "of-crate-name-line";
  nameLine.append(textSpan("FREE SUPPLY DROP", "of-crate-name"), textSpan(freeDropState(snapshot), "of-crate-level"));

  const effects = document.createElement("div");
  effects.className = "of-crate-effects";
  effects.append(effectChip("FLOOR", `${formatCompact(snapshot.crates.freeDrop.valueFloor)} CR`), effectChip("SPAWNS", "1-2 CORES"));
  details.append(nameLine, effects);

  const button = crateButton(snapshot.crates.freeDrop.canOpen ? "OPEN FREE" : freeDropButtonText(snapshot), snapshot.crates.freeDrop.canOpen, () =>
    runtime.dispatch({ type: "open-crate", crateId: "free" })
  );
  button.dataset.crateId = "free";

  const actions = document.createElement("div");
  actions.className = "of-crate-actions";
  actions.append(textSpan("FREE", "of-crate-price"), button);

  row.append(icon, details, actions);
  return row;
}

function createPaidCrateCard(crate: SupplyCrateCardView, runtime: OrbitForgeRuntime): HTMLElement {
  const card = document.createElement("article");
  card.className = "of-crate-row";
  card.dataset.crateId = crate.id;
  card.classList.toggle("is-locked", crate.locked);
  card.classList.toggle("is-affordable", crate.canOpen);

  const icon = createCrateIcon(crate.id);

  const details = document.createElement("div");
  details.className = "of-crate-details";

  const nameLine = document.createElement("div");
  nameLine.className = "of-crate-name-line";
  nameLine.append(textSpan(crate.name, "of-crate-name"), textSpan(`${crate.remainingOpens}/5 | ${formatDuration(crate.resetsInSeconds)}`, "of-crate-level"));

  const effects = document.createElement("div");
  effects.className = "of-crate-effects";
  effects.append(...crateRewardChips(crate));
  details.append(nameLine, effects);

  const actions = document.createElement("div");
  actions.className = "of-crate-actions";
  actions.append(textSpan(`${formatCompact(crate.price)} CR`, "of-crate-price"));
  const button = crateButton(crate.locked ? "LVL 2" : "OPEN", crate.canOpen, () => runtime.dispatch({ type: "open-crate", crateId: crate.id }));
  button.dataset.crateId = crate.id;
  actions.append(button);

  card.append(icon, details, actions);
  return card;
}

function renderOverlay(overlay: HTMLElement, snapshot: GameSnapshot, runtime: OrbitForgeRuntime): void {
  const resultView = snapshot.crates.pendingResult;
  if (!resultView) {
    overlay.replaceChildren();
    return;
  }

  const result = resultView.result;
  const shell = document.createElement("div");
  shell.className = "of-crate-reveal-shell";
  shell.classList.toggle("is-presented", resultView.presented);
  shell.classList.toggle("is-jackpot", result.jackpot || result.crateId === "jackpot");

  const artWrap = document.createElement("div");
  artWrap.className = "of-crate-reveal-art-wrap";
  if (resultView.presented) {
    artWrap.append(openCrateImage(result));
  } else {
    artWrap.append(crateImage(result.crateId));
  }

  const title = document.createElement("h2");
  title.className = "of-crate-reveal-title";
  title.textContent = resultView.presented ? resultTitle(resultView) : resultView.crateName;

  const rewards = document.createElement("div");
  rewards.className = "of-crate-reward-stack";
  if (resultView.presented) {
    rewards.append(textSpan(`+${formatCompact(result.cash)} BONUS CR`, "of-crate-reward-primary"));
    for (const line of resultView.rewardLines.slice(1)) {
      rewards.append(textSpan(line, "of-crate-reward-line"));
    }
  } else {
    rewards.append(textSpan("OPENING", "of-crate-reward-primary"));
  }

  const children: HTMLElement[] = [artWrap, title, rewards];
  if (resultView.presented) {
    const action = document.createElement("button");
    action.className = "of-crate-continue";
    action.type = "button";
    action.textContent = "CONTINUE";
    action.addEventListener("click", () => runtime.dispatch({ type: "dismiss-crate-result" }));
    children.push(action);
  }

  shell.append(...children);
  overlay.replaceChildren(shell);
}

function crateButton(label: string, active: boolean, action: () => void): HTMLButtonElement {
  const button = document.createElement("button");
  button.className = "of-crate-open";
  button.type = "button";
  button.textContent = label;
  button.setAttribute("aria-disabled", active ? "false" : "true");
  button.addEventListener("click", () => action());
  return button;
}

function createCrateIcon(crateId: SupplyCrateId): HTMLElement {
  const icon = document.createElement("span");
  icon.className = "of-crate-icon";
  icon.append(crateImage(crateId));
  return icon;
}

function crateImage(crateId: SupplyCrateId): HTMLImageElement {
  const image = document.createElement("img");
  image.className = "of-crate-art";
  image.src = crateId === "jackpot" ? assetManifest.supplyCrateJackpot.url : assetManifest.supplyCrateClosed.url;
  image.alt = "";
  return image;
}

function openCrateImage(result: SupplyCrateResult): HTMLImageElement {
  const image = document.createElement("img");
  image.className = "of-crate-reward-preview";
  image.src = openedCrateUrl(result);
  image.alt = "";
  return image;
}

function openedCrateUrl(result: SupplyCrateResult): string {
  if (result.jackpot || result.crateId === "jackpot") {
    return assetManifest.supplyCrateOpenJackpot.url;
  }
  if (result.crateId === "neon") {
    return assetManifest.supplyCrateOpenNeon.url;
  }
  return assetManifest.supplyCrateOpen.url;
}

function freeDropState(snapshot: GameSnapshot): string {
  if (snapshot.crates.pendingBlocksOpen) {
    return "OPENING";
  }
  if (snapshot.crates.freeDrop.ready) {
    return "READY";
  }
  return `READY IN ${formatDuration(snapshot.crates.freeDrop.remainingSeconds)}`;
}

function freeDropButtonText(snapshot: GameSnapshot): string {
  if (snapshot.crates.pendingBlocksOpen) {
    return "OPENING";
  }
  return snapshot.crates.freeDrop.ready ? "OPEN FREE" : formatDuration(snapshot.crates.freeDrop.remainingSeconds);
}
